import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ReactReduxContext } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import Axios from "axios";
import {
    Button,
    Drawer,
    Skeleton,
    Typography,
    Tag,
    Result,
    Input,
    Select,
    Pagination,
} from "antd";
import {
    ArrowLeftOutlined,
    CloseOutlined,
    ReloadOutlined,
    SearchOutlined,
    SafetyCertificateOutlined,
    UnorderedListOutlined,
    AppstoreOutlined,
} from "@ant-design/icons";
import { getFormattedAmount, formatSmartPrice } from "Shared/Utils";
import getCurrencySymbol from "Shared/CurrancySymbol";
import { Tokens } from "Shared/Tokens";
import { formatCutoffLabel } from "Components/Drawers/CutoffDateDrawer";
import LedgerEventDetailDrawer from "Components/Drawers/LedgerEventDetailDrawer";
import MoneyLedgerView from "Components/ListView/MoneyLedgerView";
import LoginWall from "Components/Shared/LoginWall";
import AppShell, { AutoSyncBadge } from "Components/Layout/AppShell";
import { subscribeToLedgerSync } from "utils/Firebase/ledgerSync";

const { Title } = Typography;
const POLL_INTERVAL_MS = 3000;

const SORT_OPTIONS = [
    { value: "netpl", label: "Net P&L" },
    { value: "roi", label: "ROI %" },
    { value: "invested", label: "Invested" },
    { value: "name", label: "Name" },
];

const sortCoins = (coins, sortBy) => {
    const sorted = [...coins];
    if (sortBy === "roi") {
        sorted.sort(
            (a, b) =>
                (b.roiPercentage ?? -Infinity) - (a.roiPercentage ?? -Infinity)
        );
    } else if (sortBy === "invested") {
        sorted.sort(
            (a, b) => (b.totalBoughtCost || 0) - (a.totalBoughtCost || 0)
        );
    } else if (sortBy === "name") {
        sorted.sort((a, b) => a.name.localeCompare(b.name));
    } else {
        sorted.sort((a, b) => (b.totalPL || 0) - (a.totalPL || 0));
    }
    return sorted;
};

const signed = (value) => ((value || 0) >= 0 ? "+" : "-");

const BinanceReportCard = () => {
    const history = useHistory();
    const location = useLocation();
    const pollTimer = useRef(null);
    const reduxContext = useContext(ReactReduxContext);
    const [, forceUpdate] = useState(0);

    useEffect(() => {
        if (!reduxContext?.store?.subscribe) return undefined;
        return reduxContext.store.subscribe(() => {
            forceUpdate((n) => n + 1);
        });
    }, [reduxContext]);

    const storeState = reduxContext?.store?.getState?.() || {};
    const settings = storeState.settings || {};
    const manualAssets = storeState.assets?.assets || [];
    const manualPortfolios = storeState.portfolios?.portfolios || [];
    const selectedCurrency =
        settings?.binanceCurrency || settings?.currency || "USDT";
    const currencySymbol = getCurrencySymbol(selectedCurrency);
    const isUsdPegged =
        selectedCurrency.toUpperCase() === "USDT" ||
        selectedCurrency.toUpperCase() === "USD";
    const rawRate = Number(settings?.currencyExchangeRate);
    const exchangeRate = !isUsdPegged && rawRate > 0 ? rawRate : 1;

    const money = (value) =>
        `${currencySymbol}${getFormattedAmount(
            Math.abs((value || 0) * exchangeRate)
        )}`;

    const smartMoney = (value) =>
        `${currencySymbol}${formatSmartPrice(
            Math.abs((value || 0) * exchangeRate)
        )}`;

    let initialViewMode = "coins";
    if (typeof location?.search === "string") {
        if (location.search.includes("view=tradebook")) {
            initialViewMode = "tradebook";
        } else if (location.search.includes("view=ledger")) {
            initialViewMode = "ledger";
        }
    }

    const [status, setStatus] = useState("loading");
    const [errorMessage, setErrorMessage] = useState("");
    const [reportCard, setReportCard] = useState(null);
    const [ledgerData, setLedgerData] = useState(null);
    const [viewMode, setViewMode] = useState(initialViewMode);
    const [syncUserId, setSyncUserId] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("netpl");
    const [positionFilter, setPositionFilter] = useState("all");
    const [coinsPage, setCoinsPage] = useState(1);
    const coinsPageSize = 10;
    const cutoffDate = settings?.binanceCutoffDate || "2026-01-01";
    const [selectedCoin, setSelectedCoin] = useState(null);
    const [selectedLedgerEvent, setSelectedLedgerEvent] = useState(null);
    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined" ? window.innerWidth < 768 : false
    );

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const goBack = () => history.push("/imported/binance");

    const clearPoll = () => {
        if (pollTimer.current) {
            clearTimeout(pollTimer.current);
            pollTimer.current = null;
        }
    };

    const fetchLedgerTimeline = async (cutoffOverride = null) => {
        const effectiveCutoff =
            cutoffOverride ||
            cutoffDate ||
            settings?.binanceCutoffDate ||
            "2026-01-01";
        try {
            const api = `${process.env.REACT_APP_API}/binance/ledger`;
            const response = await Promise.resolve(
                Axios.get(api, {
                    params: { cutoffDate: effectiveCutoff },
                    withCredentials: true,
                })
            );
            const data = response?.data?.data;
            if (data && data.status === "ready") {
                setLedgerData(data);
            }
        } catch (err) {
            // keep existing ledger state
        }
    };

    const fetchReportCard = async (cutoffOverride = null) => {
        const effectiveCutoff =
            cutoffOverride ||
            cutoffDate ||
            settings?.binanceCutoffDate ||
            "2026-01-01";
        try {
            const api = `${process.env.REACT_APP_API}/binance/report-card`;
            const response = await Axios.get(api, {
                params: { cutoffDate: effectiveCutoff },
                withCredentials: true,
            });
            const data = response.data?.data;
            if (data?.userId) {
                setSyncUserId(data.userId);
            }

            if (data?.status === "syncing") {
                setStatus("syncing");
                clearPoll();
                pollTimer.current = setTimeout(
                    () => fetchReportCard(effectiveCutoff),
                    POLL_INTERVAL_MS
                );
                return;
            }
            if (data?.status === "error") {
                setStatus("error");
                setErrorMessage(data.error || "Sync failed");
                return;
            }
            setReportCard(data);
            if (data?.ledger) {
                setLedgerData(data.ledger);
            }
            setStatus("ready");
        } catch (error) {
            if (error.response?.status === 401) {
                setStatus("unauthenticated");
                return;
            }
            setStatus("error");
            setErrorMessage(
                error.response?.status === 403
                    ? "Connect your Binance account first."
                    : "Couldn't load the report card."
            );
        }
    };

    const handleManualSync = async () => {
        setSyncing(true);
        try {
            const api = `${process.env.REACT_APP_API}/binance/report-card/sync`;
            await Axios.post(api, null, { withCredentials: true });
            setStatus("syncing");
            clearPoll();
            pollTimer.current = setTimeout(fetchReportCard, POLL_INTERVAL_MS);
        } catch (error) {
            // leave current state — user can retry
        }
        setSyncing(false);
    };

    useEffect(() => {
        fetchReportCard(cutoffDate);
        return clearPoll;
        // eslint-disable-next-line
    }, [cutoffDate]);

    useEffect(() => {
        if (status !== "syncing") {
            return undefined;
        }
        const unsubscribe = subscribeToLedgerSync(
            syncUserId || reportCard?.userId || "default",
            (docData) => {
                if (docData?.status === "ready") {
                    fetchReportCard();
                }
            }
        );
        return () => {
            if (typeof unsubscribe === "function") {
                unsubscribe();
            }
        };
        // eslint-disable-next-line
    }, [status, syncUserId, reportCard?.userId]);

    const isCoinActive = (c) =>
        (Number(c?.openQuantity || 0) > 1e-8 &&
            Number(c?.exchangeQuantity || 0) > 1e-8) ||
        Number(c?.openCostBasis || 0) > 0.01;

    const allCoins = useMemo(() => {
        const perCoin = reportCard?.perCoin || {};
        return Object.entries(perCoin).map(([name, c]) => ({ name, ...c }));
    }, [reportCard]);

    const activeCoinsCount = useMemo(
        () => allCoins.filter((c) => isCoinActive(c)).length,
        [allCoins]
    );
    const closedCoinsCount = useMemo(
        () => allCoins.filter((c) => !isCoinActive(c)).length,
        [allCoins]
    );

    const visibleCoins = useMemo(() => {
        const filtered = allCoins.filter((c) => {
            if (positionFilter === "active" && !isCoinActive(c)) return false;
            if (positionFilter === "closed" && isCoinActive(c)) return false;
            return c.name
                .toLowerCase()
                .includes(searchQuery.trim().toLowerCase());
        });
        return sortCoins(filtered, sortBy);
    }, [allCoins, searchQuery, sortBy, positionFilter]);

    const pagedCoins = useMemo(() => {
        const startIdx = (coinsPage - 1) * coinsPageSize;
        return visibleCoins.slice(startIdx, startIdx + coinsPageSize);
    }, [visibleCoins, coinsPage, coinsPageSize]);

    const header = (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 24px",
                backgroundColor: Tokens.bg,
                borderBottom: `1px solid ${Tokens.hairline}`,
                height: "64px",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    maxWidth: Tokens.containerMaxWidth,
                    margin: "0 auto",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        minWidth: 0,
                    }}
                >
                    <Button
                        type="text"
                        icon={
                            <ArrowLeftOutlined
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "16px",
                                }}
                            />
                        }
                        onClick={goBack}
                        title="Back to portfolio"
                        style={{
                            padding: 0,
                            width: "32px",
                            height: "32px",
                            flexShrink: 0,
                        }}
                    />
                    <span
                        style={{
                            fontSize: "16px",
                            fontWeight: 500,
                            color: Tokens.textPrimary,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        Account Summary
                    </span>
                    <AutoSyncBadge />
                </div>
                {status === "ready" && (
                    <Button
                        type="text"
                        icon={
                            <ReloadOutlined
                                style={{ color: Tokens.textSecondary }}
                            />
                        }
                        onClick={handleManualSync}
                        loading={syncing}
                        title="Re-sync from Binance"
                    />
                )}
            </div>
        </div>
    );

    const renderMismatchBadge = () => (
        <Tag
            title="Balance includes deposits, rewards, or transfers outside spot trades"
            style={{
                margin: 0,
                backgroundColor: Tokens.warningBg,
                color: Tokens.warning,
                border: `1px solid ${Tokens.warningBorder}`,
                borderRadius: Tokens.radiusTag,
                fontFamily: Tokens.fontMono,
                fontSize: "11px",
                padding: "0 6px",
            }}
        >
            deposit / transfer
        </Tag>
    );

    const renderDetailDrawer = () => {
        if (!selectedCoin) return null;
        const coin = selectedCoin;
        return (
            <Drawer
                open
                push={false}
                onClose={() => setSelectedCoin(null)}
                placement={isMobile ? "bottom" : "right"}
                width={isMobile ? "100%" : 460}
                height={isMobile ? "82%" : "100%"}
                closable
                closeIcon={
                    <CloseOutlined style={{ color: Tokens.textSecondary }} />
                }
                maskClosable
                maskStyle={{ backgroundColor: "rgba(0, 0, 0, 0.75)" }}
                title={
                    <span
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            color: Tokens.textPrimary,
                        }}
                    >
                        {coin.name}
                        {!coin.reconciled && renderMismatchBadge()}
                    </span>
                }
                bodyStyle={{
                    backgroundColor: Tokens.bg,
                    padding: "24px 16px",
                }}
                headerStyle={{
                    backgroundColor: Tokens.bg,
                    borderBottom: `1px solid ${Tokens.hairline}`,
                }}
                footerStyle={{
                    backgroundColor: Tokens.card,
                    borderTop: `1px solid ${Tokens.hairline}`,
                    padding: "12px 16px",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                        maxWidth: Tokens.containerMaxWidth,
                        margin: "0 auto",
                        width: "100%",
                    }}
                >
                    <div>
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "12px",
                            }}
                        >
                            Total P&amp;L
                        </div>
                        <Title
                            level={3}
                            style={{
                                margin: "4px 0 0",
                                fontWeight: 500,
                                fontVariantNumeric: "tabular-nums",
                                color:
                                    (coin.totalPL || 0) >= 0
                                        ? Tokens.positive
                                        : Tokens.negative,
                            }}
                        >
                            {signed(coin.totalPL)}
                            {money(coin.totalPL)}
                        </Title>
                        <div
                            style={{
                                color: Tokens.textMuted,
                                fontSize: "12px",
                                fontFamily: Tokens.fontMono,
                                fontVariantNumeric: "tabular-nums",
                                marginTop: "4px",
                            }}
                        >
                            {coin.roiPercentage === null
                                ? "ROI unavailable — no purchase cost recorded"
                                : `${
                                      coin.roiPercentage >= 0 ? "+" : ""
                                  }${coin.roiPercentage.toFixed(2)}% ROI`}
                        </div>
                    </div>

                    <div
                        style={{
                            backgroundColor: Tokens.card,
                            border: `1px solid ${Tokens.hairline}`,
                            borderRadius: Tokens.radiusCard,
                            padding: "16px",
                        }}
                    >
                        {[
                            [
                                "Invested (lifetime)",
                                money(coin.totalBoughtCost),
                                null,
                            ],
                            [
                                "Open cost basis",
                                money(coin.openCostBasis),
                                null,
                            ],
                            [
                                "Realized P&L",
                                `${signed(coin.realizedPL)}${money(
                                    coin.realizedPL
                                )}`,
                                coin.realizedPL >= 0
                                    ? Tokens.positive
                                    : Tokens.negative,
                            ],
                            [
                                "Unrealized P&L",
                                `${signed(coin.unrealizedPL)}${money(
                                    coin.unrealizedPL
                                )}`,
                                coin.unrealizedPL >= 0
                                    ? Tokens.positive
                                    : Tokens.negative,
                            ],
                            ["Current value", money(coin.currentValue), null],
                            [
                                "Current price",
                                smartMoney(coin.currentPrice),
                                null,
                            ],
                        ].map(([label, value, color]) => (
                            <div
                                key={label}
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    padding: "6px 0",
                                    fontSize: "13px",
                                }}
                            >
                                <span style={{ color: Tokens.textSecondary }}>
                                    {label}
                                </span>
                                <span
                                    style={{
                                        color: color || Tokens.textPrimary,
                                        fontFamily: Tokens.fontMono,
                                        fontVariantNumeric: "tabular-nums",
                                    }}
                                >
                                    {value}
                                </span>
                            </div>
                        ))}
                    </div>

                    {!coin.reconciled && (
                        <div
                            style={{
                                backgroundColor: Tokens.warningBg,
                                border: `1px solid ${Tokens.warningBorder}`,
                                borderRadius: Tokens.radiusCard,
                                padding: "12px 16px",
                                color: Tokens.warning,
                                fontSize: "12px",
                                lineHeight: "1.5",
                            }}
                        >
                            We track {coin.openQuantity} {coin.name} still open;
                            Binance shows {coin.exchangeQuantity} on your
                            exchange balance. Unrealized P&amp;L above uses our
                            tracked quantity, not the exchange balance, and this
                            coin isn&apos;t counted in the account-wide Net
                            P&amp;L total.
                        </div>
                    )}

                    {/* Reconciliation & Quantity Proof */}
                    <div
                        data-testid="coin-reconciliation-proof"
                        style={{
                            backgroundColor: Tokens.card,
                            border: `1px solid ${Tokens.hairline}`,
                            borderRadius: Tokens.radiusCard,
                            padding: "14px 16px",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "8px",
                            }}
                        >
                            <span
                                style={{
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: Tokens.textPrimary,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                }}
                            >
                                <SafetyCertificateOutlined
                                    style={{
                                        color: coin.reconciled
                                            ? Tokens.positive
                                            : Tokens.warning,
                                    }}
                                />
                                <span>Reconciliation Proof</span>
                            </span>
                            <span
                                style={{
                                    fontSize: "11px",
                                    fontFamily: Tokens.fontMono,
                                    color: coin.reconciled
                                        ? Tokens.positive
                                        : Tokens.warning,
                                }}
                            >
                                {coin.reconciled
                                    ? "100% Verified"
                                    : "Transfer Gap"}
                            </span>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: "12px",
                                padding: "4px 0",
                            }}
                        >
                            <span style={{ color: Tokens.textSecondary }}>
                                Ledger Open Qty
                            </span>
                            <span
                                style={{
                                    color: Tokens.textPrimary,
                                    fontFamily: Tokens.fontMono,
                                }}
                            >
                                {Number(coin.openQuantity || 0).toLocaleString(
                                    "en-US",
                                    { maximumFractionDigits: 6 }
                                )}{" "}
                                {coin.name}
                            </span>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: "12px",
                                padding: "4px 0",
                            }}
                        >
                            <span style={{ color: Tokens.textSecondary }}>
                                Live Binance Balance (Spot+Earn+Funding)
                            </span>
                            <span
                                style={{
                                    color: Tokens.textPrimary,
                                    fontFamily: Tokens.fontMono,
                                }}
                            >
                                {Number(
                                    coin.exchangeQuantity ??
                                        coin.openQuantity ??
                                        0
                                ).toLocaleString("en-US", {
                                    maximumFractionDigits: 6,
                                })}{" "}
                                {coin.name}
                            </span>
                        </div>
                    </div>

                    {/* Coin Chronological Audit Trail */}
                    {Array.isArray(ledgerData?.events) && (
                        <div
                            data-testid="coin-audit-trail"
                            style={{
                                backgroundColor: Tokens.card,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusCard,
                                padding: "14px 16px",
                            }}
                        >
                            <div
                                style={{
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: Tokens.textPrimary,
                                    marginBottom: "10px",
                                }}
                            >
                                Chronological Audit Trail ({coin.name})
                            </div>
                            {ledgerData.events
                                .filter((ev) => ev.coin === coin.name)
                                .slice(0, 15)
                                .map((ev, idx) => (
                                    <div
                                        key={`${ev.id}-${ev.time}`}
                                        role="button"
                                        tabIndex={0}
                                        data-testid="coin-audit-event-row"
                                        onClick={() =>
                                            setSelectedLedgerEvent(ev)
                                        }
                                        onKeyPress={(e) => {
                                            if (e.key === "Enter") {
                                                setSelectedLedgerEvent(ev);
                                            }
                                        }}
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            padding: "8px 0",
                                            borderTop:
                                                idx > 0
                                                    ? `1px solid ${Tokens.hairline}`
                                                    : "none",
                                            cursor: "pointer",
                                            fontSize: "12px",
                                        }}
                                    >
                                        <div>
                                            <div
                                                style={{
                                                    fontWeight: 500,
                                                    color: Tokens.textPrimary,
                                                    fontFamily: Tokens.fontMono,
                                                }}
                                            >
                                                {String(
                                                    ev.source || ev.type
                                                ).toUpperCase()}{" "}
                                                ·{" "}
                                                {ev.type === "sell" ||
                                                ev.type === "withdrawal"
                                                    ? "-"
                                                    : "+"}
                                                {Number(
                                                    ev.qty || 0
                                                ).toLocaleString("en-US", {
                                                    maximumFractionDigits: 4,
                                                })}{" "}
                                                {coin.name}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: "10px",
                                                    color: Tokens.textMuted,
                                                    fontFamily: Tokens.fontMono,
                                                }}
                                            >
                                                Cost:{" "}
                                                {money(ev.beforeCostBasis)} →{" "}
                                                {money(ev.afterCostBasis)}
                                            </div>
                                        </div>
                                        <div
                                            style={{
                                                textAlign: "right",
                                                fontFamily: Tokens.fontMono,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    color: Tokens.textPrimary,
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {money(ev.quoteQty)}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: "10px",
                                                    color: Tokens.accent,
                                                }}
                                            >
                                                Inspect →
                                            </div>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            </Drawer>
        );
    };

    const renderBody = () => {
        if (status === "loading") {
            return <Skeleton active paragraph={{ rows: 6 }} />;
        }

        if (status === "syncing") {
            return (
                <Result
                    status="info"
                    title="Building your account history"
                    subTitle="Pulling your full Binance trade history for the first time — this can take a couple of minutes. This page will update automatically."
                />
            );
        }

        if (status === "unauthenticated") {
            return (
                <LoginWall
                    title="Sign in to view Account Summary"
                    description="Sign in to unlock your complete Binance Account Summary, audited USDT running balance ledger, and tradebook."
                    features={[
                        "Coin-by-coin realized & unrealized P&L breakdown",
                        "Audited Account USDT Ledger with running balance",
                        "Complete chronological Tradebook across Spot, Auto-Invest & Convert",
                    ]}
                    returnPath="/imported/binance/report-card"
                />
            );
        }

        if (status === "error") {
            return (
                <Result
                    status="warning"
                    title="Couldn't load the report card"
                    subTitle={errorMessage}
                    extra={
                        <Button type="primary" onClick={handleManualSync}>
                            Try again
                        </Button>
                    }
                />
            );
        }

        const {
            lifetimeInvested = 0,
            openInvested,
            lifetimeRealizedPL = 0,
            lifetimeUnrealizedPL = 0,
            lifetimeNetPL = 0,
            feesCollected = 0,
            dustCoinsIgnored = 0,
        } = reportCard || {};

        const computedOpenInvested =
            Number.isFinite(Number(openInvested)) && Number(openInvested) > 0
                ? Number(openInvested)
                : allCoins.reduce(
                      (sum, c) => sum + Number(c.openCostBasis || 0),
                      0
                  );
        const isNetPositive = lifetimeNetPL >= 0;

        return (
            <React.Fragment>
                {/* Top Segmented Mode Switcher: Coins vs Ledger vs Tradebook */}
                <div
                    data-testid="report-view-mode"
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                        gap: "6px",
                        backgroundColor: Tokens.card,
                        border: `1px solid ${Tokens.hairline}`,
                        borderRadius: Tokens.radiusControl,
                        padding: "4px",
                        marginBottom: "16px",
                    }}
                >
                    {[
                        {
                            key: "coins",
                            label: "Coins",
                            icon: AppstoreOutlined,
                            testId: "report-view-coins",
                        },
                        {
                            key: "ledger",
                            label: "Ledger",
                            icon: SafetyCertificateOutlined,
                            testId: "report-view-ledger",
                        },
                        {
                            key: "tradebook",
                            label: "Tradebook",
                            icon: UnorderedListOutlined,
                            testId: "report-view-tradebook",
                        },
                    ].map((tab) => {
                        const IconComp = tab.icon;
                        const active = viewMode === tab.key;
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                data-testid={tab.testId}
                                onClick={() => {
                                    setViewMode(tab.key);
                                    if (
                                        (tab.key === "ledger" ||
                                            tab.key === "tradebook") &&
                                        !ledgerData
                                    ) {
                                        fetchLedgerTimeline(cutoffDate);
                                    }
                                }}
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "6px",
                                    minHeight: "38px",
                                    padding: "6px 8px",
                                    borderRadius: Tokens.radiusControl,
                                    border: active
                                        ? `1px solid ${Tokens.accent}`
                                        : "1px solid transparent",
                                    backgroundColor: active
                                        ? Tokens.accentSubtle
                                        : "transparent",
                                    color: active
                                        ? Tokens.textPrimary
                                        : Tokens.textSecondary,
                                    fontSize: "13px",
                                    fontWeight: active ? 600 : 500,
                                    whiteSpace: "nowrap",
                                    cursor: "pointer",
                                }}
                            >
                                <IconComp
                                    style={{ fontSize: "13px", flexShrink: 0 }}
                                />
                                <span style={{ whiteSpace: "nowrap" }}>
                                    {tab.label}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {viewMode === "ledger" || viewMode === "tradebook" ? (
                    <MoneyLedgerView
                        ledgerData={ledgerData || reportCard?.ledger}
                        reportCard={reportCard}
                        manualAssets={manualAssets}
                        portfolios={manualPortfolios}
                        cutoffDate={cutoffDate}
                        currencySymbol={currencySymbol}
                        exchangeRate={exchangeRate}
                        bookMode={viewMode}
                        onBookModeChange={setViewMode}
                        timezone={settings?.timezone || "Asia/Kolkata"}
                    />
                ) : (
                    <React.Fragment>
                        <div
                            style={{
                                backgroundColor: Tokens.card,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusPanel,
                                padding: "20px",
                                marginBottom: "16px",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: "8px",
                                    flexWrap: "wrap",
                                }}
                            >
                                <div
                                    style={{
                                        color: Tokens.textSecondary,
                                        fontSize: "13px",
                                    }}
                                >
                                    Net P&amp;L ·{" "}
                                    {formatCutoffLabel(cutoffDate)}
                                </div>
                            </div>
                            <Title
                                level={1}
                                style={{
                                    margin: "8px 0 0",
                                    fontWeight: 500,
                                    fontSize: "32px",
                                    fontVariantNumeric: "tabular-nums",
                                    color: isNetPositive
                                        ? Tokens.positive
                                        : Tokens.negative,
                                }}
                            >
                                {signed(lifetimeNetPL)}
                                {money(lifetimeNetPL)}
                            </Title>

                            <div
                                style={{
                                    borderTop: `1px solid ${Tokens.hairline}`,
                                    paddingTop: "12px",
                                    marginTop: "12px",
                                    display: "grid",
                                    gridTemplateColumns:
                                        "repeat(3, minmax(0, 1fr))",
                                    gap: "10px",
                                }}
                            >
                                <div>
                                    <div
                                        style={{
                                            color: Tokens.textMuted,
                                            fontSize: "11px",
                                            marginBottom: "2px",
                                        }}
                                    >
                                        Active invested
                                    </div>
                                    <div
                                        style={{
                                            color: Tokens.textPrimary,
                                            fontSize: "13px",
                                            fontWeight: 500,
                                            fontFamily: Tokens.fontMono,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {money(
                                            computedOpenInvested > 0
                                                ? computedOpenInvested
                                                : lifetimeInvested
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <div
                                        style={{
                                            color: Tokens.textMuted,
                                            fontSize: "11px",
                                            marginBottom: "2px",
                                        }}
                                    >
                                        Open P&amp;L
                                    </div>
                                    <div
                                        style={{
                                            color:
                                                lifetimeUnrealizedPL >= 0
                                                    ? Tokens.positive
                                                    : Tokens.negative,
                                            fontSize: "13px",
                                            fontWeight: 500,
                                            fontFamily: Tokens.fontMono,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {signed(lifetimeUnrealizedPL)}
                                        {money(lifetimeUnrealizedPL)}
                                    </div>
                                </div>
                                <div>
                                    <div
                                        style={{
                                            color: Tokens.textMuted,
                                            fontSize: "11px",
                                            marginBottom: "2px",
                                        }}
                                    >
                                        Realized P&amp;L
                                    </div>
                                    <div
                                        style={{
                                            color:
                                                lifetimeRealizedPL >= 0
                                                    ? Tokens.positive
                                                    : Tokens.negative,
                                            fontSize: "13px",
                                            fontWeight: 500,
                                            fontFamily: Tokens.fontMono,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {signed(lifetimeRealizedPL)}
                                        {money(lifetimeRealizedPL)}
                                    </div>
                                </div>
                            </div>

                            <div
                                style={{
                                    color: Tokens.textMuted,
                                    fontSize: "11px",
                                    marginTop: "10px",
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                Invested {money(lifetimeInvested)} · Realized{" "}
                                {signed(lifetimeRealizedPL)}
                                {money(lifetimeRealizedPL)}
                                {dustCoinsIgnored > 0 &&
                                    ` · ${money(
                                        feesCollected
                                    )} in ${dustCoinsIgnored} dust coin${
                                        dustCoinsIgnored > 1 ? "s" : ""
                                    } written off as fees`}
                            </div>
                        </div>

                        <div
                            style={{
                                display: "flex",
                                gap: "8px",
                                marginBottom: "12px",
                            }}
                        >
                            {[
                                {
                                    key: "all",
                                    label: `All (${allCoins.length})`,
                                },
                                {
                                    key: "active",
                                    label: `Active (${activeCoinsCount})`,
                                },
                                {
                                    key: "closed",
                                    label: `Closed (${closedCoinsCount})`,
                                },
                            ].map((tab) => {
                                const active = positionFilter === tab.key;
                                return (
                                    <button
                                        key={tab.key}
                                        type="button"
                                        data-testid={`report-filter-${tab.key}`}
                                        onClick={() => {
                                            setPositionFilter(tab.key);
                                            setCoinsPage(1);
                                        }}
                                        style={{
                                            flex: 1,
                                            minHeight: "34px",
                                            padding: "4px 8px",
                                            borderRadius: Tokens.radiusControl,
                                            border: `1px solid ${
                                                active
                                                    ? Tokens.accent
                                                    : Tokens.hairline
                                            }`,
                                            backgroundColor: active
                                                ? "rgba(217, 119, 6, 0.12)"
                                                : Tokens.card,
                                            color: active
                                                ? Tokens.textPrimary
                                                : Tokens.textSecondary,
                                            fontSize: "12px",
                                            fontWeight: active ? 500 : 400,
                                            fontFamily: Tokens.fontMono,
                                            fontVariantNumeric: "tabular-nums",
                                            cursor: "pointer",
                                        }}
                                    >
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </div>

                        <div
                            style={{
                                display: "flex",
                                gap: "8px",
                                marginBottom: "16px",
                            }}
                        >
                            <Input
                                prefix={
                                    <SearchOutlined
                                        style={{ color: Tokens.textMuted }}
                                    />
                                }
                                placeholder="Search coins"
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setCoinsPage(1);
                                }}
                                style={{
                                    backgroundColor: Tokens.card,
                                    borderColor: Tokens.hairline,
                                    borderRadius: Tokens.radiusControl,
                                }}
                            />
                            <Select
                                value={sortBy}
                                onChange={(val) => {
                                    setSortBy(val);
                                    setCoinsPage(1);
                                }}
                                options={SORT_OPTIONS}
                                style={{ width: 152 }}
                            />
                        </div>

                        {pagedCoins.map((coin) => {
                            const totalPositive = (coin.totalPL || 0) >= 0;
                            const active = isCoinActive(coin);
                            return (
                                <div
                                    key={`report-${coin.name}`}
                                    onClick={() => setSelectedCoin(coin)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyPress={(e) => {
                                        if (e.key === "Enter")
                                            setSelectedCoin(coin);
                                    }}
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        backgroundColor: Tokens.card,
                                        border: `1px solid ${Tokens.hairline}`,
                                        borderRadius: Tokens.radiusCard,
                                        padding: "12px 14px",
                                        marginBottom: "8px",
                                        cursor: "pointer",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "3px",
                                            minWidth: 0,
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    color: Tokens.textPrimary,
                                                    fontWeight: 500,
                                                    fontSize: "14px",
                                                }}
                                            >
                                                {coin.name}
                                            </span>
                                            <span
                                                style={{
                                                    fontSize: "10px",
                                                    fontFamily: Tokens.fontMono,
                                                    color: active
                                                        ? Tokens.accent
                                                        : Tokens.textMuted,
                                                    backgroundColor:
                                                        Tokens.surface,
                                                    padding: "1px 6px",
                                                    borderRadius:
                                                        Tokens.radiusTag,
                                                }}
                                            >
                                                {active ? "Active" : "Closed"}
                                            </span>
                                            {!coin.reconciled &&
                                                renderMismatchBadge()}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: "11px",
                                                color: Tokens.textMuted,
                                                fontFamily: Tokens.fontMono,
                                                fontVariantNumeric:
                                                    "tabular-nums",
                                            }}
                                        >
                                            {active
                                                ? `Open ${money(
                                                      coin.openCostBasis
                                                  )} · Realized ${signed(
                                                      coin.realizedPL
                                                  )}${money(coin.realizedPL)}`
                                                : `Closed · Bought ${money(
                                                      coin.totalBoughtCost
                                                  )}`}
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "flex-end",
                                            gap: "2px",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <span
                                            style={{
                                                color: totalPositive
                                                    ? Tokens.positive
                                                    : Tokens.negative,
                                                fontWeight: 500,
                                                fontSize: "14px",
                                                fontFamily: Tokens.fontMono,
                                                fontVariantNumeric:
                                                    "tabular-nums",
                                            }}
                                        >
                                            {signed(coin.totalPL)}
                                            {money(coin.totalPL)}
                                        </span>
                                        {coin.roiPercentage !== null &&
                                            coin.roiPercentage !==
                                                undefined && (
                                                <span
                                                    style={{
                                                        fontSize: "11px",
                                                        color: Tokens.textMuted,
                                                        fontFamily:
                                                            Tokens.fontMono,
                                                        fontVariantNumeric:
                                                            "tabular-nums",
                                                    }}
                                                >
                                                    {coin.roiPercentage >= 0
                                                        ? "+"
                                                        : ""}
                                                    {coin.roiPercentage.toFixed(
                                                        1
                                                    )}
                                                    %
                                                </span>
                                            )}
                                    </div>
                                </div>
                            );
                        })}

                        {visibleCoins.length > 0 && (
                            <div
                                data-testid="report-coins-pagination"
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginTop: "12px",
                                    marginBottom: "8px",
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: "12px",
                                        color: Tokens.textMuted,
                                        fontFamily: Tokens.fontMono,
                                    }}
                                >
                                    {visibleCoins.length}{" "}
                                    {visibleCoins.length === 1
                                        ? "coin"
                                        : "coins"}
                                </span>
                                <Pagination
                                    current={coinsPage}
                                    pageSize={coinsPageSize}
                                    total={visibleCoins.length}
                                    onChange={(page) => setCoinsPage(page)}
                                    size="small"
                                    showSizeChanger={false}
                                />
                            </div>
                        )}

                        {visibleCoins.length === 0 && allCoins.length > 0 && (
                            <div
                                style={{
                                    color: Tokens.textSecondary,
                                    textAlign: "center",
                                    padding: "40px 0",
                                }}
                            >
                                No coins match &quot;{searchQuery}&quot;
                            </div>
                        )}

                        {allCoins.length === 0 && (
                            <div
                                style={{
                                    color: Tokens.textSecondary,
                                    textAlign: "center",
                                    padding: "40px 0",
                                }}
                            >
                                No trade history found yet
                            </div>
                        )}
                    </React.Fragment>
                )}
            </React.Fragment>
        );
    };

    return (
        <AppShell
            activeTab="report-card"
            bodyStyle={{ backgroundColor: Tokens.bg, padding: "0 0 88px" }}
        >
            {header}
            <div
                style={{
                    padding: "24px 16px",
                    maxWidth: Tokens.containerMaxWidth,
                    margin: "0 auto",
                }}
            >
                {renderBody()}
            </div>
            {renderDetailDrawer()}
            <LedgerEventDetailDrawer
                event={selectedLedgerEvent}
                open={Boolean(selectedLedgerEvent)}
                onClose={() => setSelectedLedgerEvent(null)}
                currencySymbol={currencySymbol}
                exchangeRate={exchangeRate}
            />
        </AppShell>
    );
};

export default BinanceReportCard;
